import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getJSON } from "@/lib/api";
import { youTubeEmbed } from "@/lib/youtube";
import { Seo } from "@/components/Seo";
import { SITE_URL } from "@/lib/seo";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

export default function LabsPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    window.scrollTo(0, 0);
    getJSON(`/labs/${slug}`)
      .then(setPost)
      .catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-[var(--muted)]">
        Loading…
      </div>
    );
  }

  if (post === null) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 text-center">
        <h1 className="t-section text-[var(--ink)]">Post not found</h1>
        <Link to="/labs" className="mt-6 inline-flex items-center gap-2 font-semibold text-[var(--green-ink)] hover:underline">
          <ArrowLeft size={16} /> Back to Labs
        </Link>
      </div>
    );
  }

  const embed = youTubeEmbed(post.youtube_url);
  const paragraphs = (post.content || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <article className="bg-[var(--bg-base)] pb-24">
      <Seo
        title={post.title}
        description={(post.excerpt || post.content || "").slice(0, 160)}
        path={`/labs/${post.slug}`}
        type="article"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: (post.excerpt || "").slice(0, 250),
          author: { "@type": "Organization", name: post.author || "The AI Dept." },
          publisher: { "@type": "Organization", name: "The AI Dept.", logo: { "@type": "ImageObject", url: `${SITE_URL}/theaidept-logo.png` } },
          datePublished: post.published_at || undefined,
          mainEntityOfPage: `${SITE_URL}/labs/${post.slug}`,
        }}
      />
      {/* Header */}
      <header className="bg-[var(--charcoal)] pb-14 pt-36 text-white md:pt-44">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <Link to="/labs" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--mint)] hover:underline" data-testid="labs-back">
              <ArrowLeft size={15} /> Labs
            </Link>
          </Reveal>
          {post.category && (
            <Reveal delay={60}>
              <span className="mt-6 block text-xs font-semibold uppercase tracking-[0.16em] text-[var(--mint)]">
                {post.category}
              </span>
            </Reveal>
          )}
          <Reveal delay={100}>
            <h1 className="mt-4 t-hero" data-testid="labs-post-title">{post.title}</h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 text-sm text-white/60">
              {post.author}
              {post.read_time ? ` · ${post.read_time}` : ""}
              {post.published_at ? ` · ${fmtDate(post.published_at)}` : ""}
            </p>
          </Reveal>
        </div>
      </header>

      {/* Video — every Labs post has one */}
      {embed && (
        <div className="mx-auto -mt-8 max-w-4xl px-6">
          <div className="relative aspect-video overflow-hidden rounded-2xl bg-black shadow-[0_30px_70px_-30px_rgba(0,0,0,0.6)]" data-testid="labs-post-video">
            <iframe
              src={embed}
              title={post.title}
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Body */}
      <div className="mx-auto mt-14 max-w-3xl px-6" data-testid="labs-post-body">
        {post.excerpt && (
          <p className="mb-8 text-xl leading-relaxed text-[var(--ink)]">
            {post.excerpt}
          </p>
        )}
        {paragraphs.map((p, i) => (
          <p key={i} className="mb-5 text-[17px] leading-8 text-[var(--ink)]/90">
            {p}
          </p>
        ))}
      </div>

      {/* CTA */}
      <div className="mx-auto mt-16 max-w-3xl px-6">
        <div className="rounded-3xl bg-[var(--charcoal)] px-8 py-12 text-center text-white md:px-14">
          <h2 className="t-sub font-semibold">See what AI is costing you to ignore.</h2>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-white/70">
            Your free Business DNA audit — six scores, real numbers, no pitch. In your inbox in days.
          </p>
          <Link
            to="/audit"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--mint)] px-8 py-3.5 text-sm font-semibold text-[var(--charcoal)] transition-transform hover:-translate-y-0.5"
            data-testid="labs-post-cta"
          >
            Get my free audit <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </article>
  );
}
